import React, { useState, useRef, useEffect } from "react";
import SimpleButton from "../Input/SimpleButton";
import { ArrowForward } from "../Icons/HistoryIcons";
import {
	JobState,
	JobStatesValues,
	DONE,
	ArchiveOutcome,
	xlate
} from "../../../DataModels/DataModels";
import { toTitleCase } from "../../../Utilities/ToTitleCase";

interface StatusMenuProps {
	onClick?: (obj: {
		jobId: string | number;
		fromState: JobState;
		toState: JobState;
		reason?: string;
		outcome?: ArchiveOutcome;
	}) => void;
	onStatusChange?: (isOpen: boolean) => void;
	badgeClass?: string;
	currentState: JobState;
	currentJobId: string | number;
}

type Phase = "closed" | "menu" | "reason" | "archive";

const OUTCOME_OPTIONS: { value: ArchiveOutcome; label: string }[] = [
	{ value: "offer_accepted", label: "Offer Accepted" },
	{ value: "offer_declined", label: "Offer Declined" },
	{ value: "withdrawn", label: "Withdrawn" },
	{ value: "ghosted", label: "Ghosted" }
];

/** Chevron that rotates 180° when open */
const Chevron: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
	<svg
		width="24"
		height="24"
		viewBox="0 0 24 24"
		xmlns="http://www.w3.org/2000/svg"
		fill="currentColor"
		className={`transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
	>
		<path d="M7.41 8.59L12 13.17L16.59 8.59L18 10L12 16L6 10L7.41 8.59Z" />
	</svg>
);

export const StatusMenu: React.FC<StatusMenuProps> = ({
	onClick = () => {},
	onStatusChange = () => {},
	badgeClass = "w-36 bg-primary-background border-primary-active border text-primary rounded-full py-1",
	currentState,
	currentJobId
}) => {
	const [phase, setPhase] = useState<Phase>("closed");
	const [pendingState, setPendingState] = useState<JobState | null>(null);
	const [reason, setReason] = useState("");
	const reasonInputRef = useRef<HTMLInputElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// Focus the reason input when entering reason/archive phase
	useEffect(() => {
		if ((phase === "reason" || phase === "archive") && reasonInputRef.current) {
			reasonInputRef.current.focus();
		}
	}, [phase]);

	// Notify parent of open/closed status
	useEffect(() => {
		onStatusChange(phase !== "closed");
	}, [phase, onStatusChange]);

	// Handle outside clicks — close menu or submit reason
	useEffect(() => {
		if (phase === "closed") return;

		const handleClickOutside = (event: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(event.target as Node)
			) {
				if (phase === "menu") {
					setPhase("closed");
				} else if (pendingState) {
					onClick({
						jobId: currentJobId,
						fromState: currentState,
						toState: pendingState,
						reason: reason.trim() || undefined
					});
					setPhase("closed");
					setPendingState(null);
					setReason("");
				}
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, [phase, pendingState, reason, onClick, currentJobId, currentState]);

	const submitAndReset = () => {
		if (pendingState) {
			onClick({
				jobId: currentJobId,
				fromState: currentState,
				toState: pendingState,
				reason: reason.trim() || undefined
			});
		}
		setPhase("closed");
		setPendingState(null);
		setReason("");
	};

	const handleBadgeClick = () => {
		if (phase === "closed") {
			setPhase("menu");
		} else if (phase === "menu") {
			setPhase("closed");
		} else {
			submitAndReset();
		}
	};

	const handleStateSelected = (toState: JobState) => {
		setPendingState(toState);
		setPhase(toState === DONE ? "archive" : "reason");
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === "Escape") {
			e.preventDefault();
			submitAndReset();
		}
	};

	const badgeLabel =
		phase === "reason" || phase === "archive"
			? pendingState === DONE
				? "Archive"
				: toTitleCase(xlate[pendingState!])
			: currentState === DONE
				? "Archived"
				: toTitleCase(xlate[currentState]);

	const isOpen = phase !== "closed";

	const menuStates = JobStatesValues.filter((value) => value !== currentState);

	return (
		<div ref={containerRef} className="flex flex-col items-center w-80">
			{/* Panel area */}
			<div className="w-full flex justify-center mb-2">
				{/* Menu phase */}
				{phase === "menu" && (
					<div className="bg-primary-background rounded-2xl border border-primary-active max-w-36 overflow-clip animate-[slideUp_200ms_ease-out]">
						{menuStates.map((state: JobState, index: number) => (
							<div
								key={index}
								className="cursor-pointer text-primary-active hover:bg-primary-accent px-3 py-2 first:rounded-t-2xl last:rounded-b-2xl transition-colors duration-150 animate-[fadeIn_200ms_ease-out_both]"
								style={{ animationDelay: `${index * 50}ms` }}
								onClick={() => handleStateSelected(state)}
							>
								<p>{toTitleCase(xlate[state])}</p>
							</div>
						))}
					</div>
				)}

				{/* Reason input phase */}
				{phase === "reason" && (
					<div className="bg-primary-background border border-primary-active rounded-2xl p-4 w-full animate-[slideUp_250ms_ease-out]">
						<div className="flex items-center gap-2">
							<input
								ref={reasonInputRef}
								type="text"
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder="Why are you moving this job? (optional)"
								className="flex-1 px-4 py-2 text-sm text-primary bg-transparent border border-primary-inactive rounded-full focus:outline-none focus:border-primary-active placeholder:text-primary-inactive transition-colors duration-200"
							/>
							<button
								onClick={submitAndReset}
								className="w-8 h-8 shrink-0 flex items-center justify-center text-primary-active hover:text-primary rounded-full transition-colors duration-150 hover:scale-110 active:scale-95"
								style={{ transition: "color 150ms, transform 150ms" }}
								aria-label="Submit"
							>
								{ArrowForward()}
							</button>
						</div>
					</div>
				)}

				{/* Archive phase */}
				{phase === "archive" && (
					<div className="bg-primary-background border border-primary-active rounded-2xl p-4 w-full animate-[slideUp_250ms_ease-out]">
						<p className="text-primary text-sm font-bold mb-3 animate-[fadeIn_200ms_ease-out]">
							What was the outcome?
						</p>
						<div className="grid grid-cols-2 gap-2 mb-4">
							{OUTCOME_OPTIONS.map((opt, index) => (
								<SimpleButton
									key={opt.value}
									onClick={() => {
										if (!pendingState) return;
										onClick({
											jobId: currentJobId,
											fromState: currentState,
											toState: pendingState,
											reason: reason.trim() || undefined,
											outcome: opt.value
										});
										setPhase("closed");
										setPendingState(null);
										setReason("");
									}}
									className="rounded-full border text-xs px-3 py-2 animate-[fadeIn_200ms_ease-out_both] border-primary-active text-primary hover:bg-primary-accent active:scale-95"
									style={{
										animationDelay: `${index * 40}ms`,
										transition:
											"background-color 200ms, color 200ms, border-color 200ms, transform 200ms"
									}}
								>
									<p>{opt.label}</p>
								</SimpleButton>
							))}
						</div>
						<div
							className="flex items-center gap-2 animate-[fadeIn_300ms_ease-out_both]"
							style={{ animationDelay: "150ms" }}
						>
							<input
								ref={reasonInputRef}
								type="text"
								value={reason}
								onChange={(e) => setReason(e.target.value)}
								onKeyDown={handleKeyDown}
								placeholder="Any notes? (optional)"
								className="flex-1 px-4 py-2 text-sm text-primary bg-transparent border border-primary-inactive rounded-full focus:outline-none focus:border-primary-active placeholder:text-primary-inactive transition-colors duration-200"
							/>
							<button
								onClick={submitAndReset}
								className="w-8 h-8 shrink-0 flex items-center justify-center text-primary-active hover:text-primary rounded-full"
								style={{ transition: "color 150ms, transform 150ms" }}
								aria-label="Submit"
							>
								{ArrowForward()}
							</button>
						</div>
					</div>
				)}
			</div>

			{/* Badge — always anchored at the bottom */}
			<SimpleButton className={badgeClass} onClick={handleBadgeClick}>
				<Chevron isOpen={isOpen} />
				<p>{badgeLabel}</p>
			</SimpleButton>
		</div>
	);
};
